/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { FileSearch, Search, Square } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Link } from 'react-router';
import { api, isAbortError, isApiError } from '../../api';
import type { LookupRecord, LookupResult, LookupSource } from '../../api/areas/lookup';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, DataTable, Field, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatDateTime, formatDuration } from '../../lib/format';
import { hubUrl } from '../../nav/hubs';
import { DEFAULT_TLDS, expandQuery, MAX_LOOKUP_NAMES as MAX_NAMES, parseTlds } from '../../lib/lookupQuery';

const STATUS_TONE: Record<LookupResult['status'], Tone> = { available: 'ok', registered: 'neutral', unknown: 'warn' };

function ageText(ms: number): string {
  return formatDuration(Math.max(1, Math.round(ms / 1000)));
}

/** Domains › Tra cứu tên miền: availability across TLDs (streamed) and the public record. */
export function DomainLookupPage() {
  const { t } = useTranslation('domains');
  useDocumentTitle(t('lookup.title'));
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [draft, setDraft] = useState(q);
  const [tldText, setTldText] = useState(params.get('tlds') ?? DEFAULT_TLDS.join(' '));
  const [rows, setRows] = useState<LookupResult[]>([]);
  const [order, setOrder] = useState<string[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [record, setRecord] = useState<string | null>(null);
  const ctrl = useRef<AbortController | null>(null);
  const tlds = parseTlds(tldText);
  const names = expandQuery(draft, tlds);

  const run = (text: string) => {
    const list = expandQuery(text, tlds);
    if (!list.length || list.length > MAX_NAMES) return;
    ctrl.current?.abort();
    const c = new AbortController();
    ctrl.current = c;
    setRows([]);
    setOrder(list);
    setError(null);
    setRunning(true);
    api
      .lookupCheck(
        { names: list },
        (r) =>
          setRows((prev) => {
            // A final answer replaces the DNS hint of the same name, never the reverse.
            if (prev.some((p) => p.name === r.name && p.final) && !r.final) return prev;
            return [...prev.filter((p) => p.name !== r.name), r];
          }),
        { signal: c.signal },
      )
      .catch((e: unknown) => {
        if (!isAbortError(e)) setError(e);
      })
      .finally(() => {
        if (ctrl.current === c) setRunning(false);
      });
  };

  // Opened with ?q= (Ctrl-K): look up at once.
  const started = useRef('');
  useEffect(() => {
    if (q && started.current !== q) {
      started.current = q;
      setDraft(q);
      run(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the URL query changes
  }, [q]);
  useEffect(() => () => ctrl.current?.abort(), []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    next.set('q', draft.trim());
    next.set('tlds', tlds.join(' '));
    started.current = draft.trim();
    setParams(next, { replace: true });
    run(draft);
  };
  const stop = () => {
    ctrl.current?.abort();
    setRunning(false);
  };

  const position = (n: string) => {
    const i = order.findIndex((o) => o === n || o.toLowerCase() === n);
    return i < 0 ? order.length : i;
  };
  const sorted = [...rows].sort((a, b) => position(a.name) - position(b.name));
  const anyPublic = rows.some((r) => r.source && !r.authoritative);

  const sourceLabel = (s: LookupSource | null) => (s ? t(`lookup.source.${s}`) : '—');
  const reasonLabel = (r: string | null) => (r ? t(`lookup.reason.${r}` as 'lookup.reason.TIMEOUT', { defaultValue: r }) : null);
  const columns: Column<LookupResult>[] = [
    {
      key: 'name',
      header: t('lookup.col.name'),
      cell: (r) => (
        <span className="flex min-w-0 flex-col">
          <span className="font-medium break-all">{r.unicode || r.name}</span>
          {r.unicode && r.unicode !== r.name ? <span className="font-mono text-xs text-muted-foreground break-all">{r.name}</span> : null}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('lookup.col.status'),
      cell: (r) => (
        <span className="flex flex-col gap-1">
          <Badge tone={r.final ? STATUS_TONE[r.status] : 'pending'}>{r.final ? t(`lookup.status.${r.status}`) : t('lookup.status.confirming')}</Badge>
          {r.fee?.premium ? <Badge tone="warn">{t('lookup.premium', { amount: r.fee.amount ?? '?', currency: r.fee.currency ?? '' })}</Badge> : null}
          {reasonLabel(r.reason) && r.status !== 'registered' ? <span className="text-xs text-muted-foreground">{reasonLabel(r.reason)}</span> : null}
        </span>
      ),
    },
    {
      key: 'source',
      header: t('lookup.col.source'),
      cell: (r) => (
        <span className="flex flex-col gap-1">
          <span className="text-sm">{sourceLabel(r.source)}</span>
          {r.source ? (
            <Badge tone={r.authoritative ? 'ok' : 'info'} icon={false}>
              {r.authoritative ? t('lookup.authoritative') : t('lookup.publicSource')}
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      key: 'age',
      header: t('lookup.col.age'),
      cell: (r) => <span className="text-xs text-muted-foreground">{r.cached ? t('lookup.cached', { age: ageText(r.age_ms) }) : t('lookup.fresh')}</span>,
    },
    { key: 'time', header: t('lookup.col.time'), numeric: true, cell: (r) => <span className="text-xs tabular-nums">{`${r.elapsed_ms} ms`}</span> },
    {
      key: 'actions',
      header: t('lookup.col.actions'),
      cell: (r) =>
        r.status === 'registered' || r.status === 'available' ? (
          <Button size="sm" variant="outline" onClick={() => setRecord(r.name)}>
            <FileSearch /> {t('lookup.openRecord')}
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title={t('lookup.title')} subtitle={t('lookup.subtitle')} />
      <Section>
        <form className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_auto] md:items-end" onSubmit={submit}>
          <Field label={t('lookup.label')} hint={t('lookup.labelHint')}>
            <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="tino" spellCheck={false} autoCapitalize="off" />
          </Field>
          <Field label={t('lookup.tlds')} hint={names.length > MAX_NAMES ? <span className="text-critical">{t('lookup.tooMany')}</span> : t('lookup.tldsHint')}>
            <Input value={tldText} onChange={(e) => setTldText(e.target.value)} spellCheck={false} autoCapitalize="off" />
          </Field>
          <div className="flex gap-2 md:pb-6">
            <Button type="submit" disabled={!names.length || names.length > MAX_NAMES}>
              <Search /> {t('lookup.submit')}
            </Button>
            {running ? (
              <Button type="button" variant="outline" onClick={stop}>
                <Square /> {t('lookup.stop')}
              </Button>
            ) : null}
          </div>
        </form>
      </Section>
      {error ? <DataError error={error} onRetry={() => run(draft)} /> : null}
      {anyPublic ? <Callout tone="info">{t('lookup.notice')}</Callout> : null}
      <Section flush>
        {running && rows.length === 0 ? (
          <Loading label={t('lookup.running')} rows={3} />
        ) : order.length === 0 ? (
          <EmptyState title={t('lookup.empty')} icon={Search} />
        ) : (
          <>
            {running ? <p className="px-5 pb-3 text-xs text-muted-foreground">{t('lookup.running')}</p> : null}
            <DataTable columns={columns} rows={sorted} rowKey={(r) => r.name} />
          </>
        )}
      </Section>
      <RecordDialog name={record} onClose={() => setRecord(null)} />
    </div>
  );
}

function RecordDialog({ name, onClose }: { name: string | null; onClose: () => void }) {
  const { t } = useTranslation('domains');
  const [state, setState] = useState<{ name: string; data?: LookupRecord; error?: unknown } | null>(null);
  useEffect(() => {
    if (!name) return;
    const c = new AbortController();
    api.lookupWhois(name, { signal: c.signal }).then(
      (data) => setState({ name, data }),
      (error: unknown) => {
        if (!isAbortError(error)) setState({ name, error });
      },
    );
    return () => c.abort();
  }, [name]);
  const rec = state?.name === name ? state?.data : undefined;
  const err = state?.name === name ? state?.error : undefined;
  return (
    <Dialog open={!!name} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="break-all">{t('lookup.record.title', { name: name ?? '' })}</DialogTitle>
          <DialogDescription>{rec ? `${t(`lookup.source.${rec.source}`)}${rec.server ? ` · ${rec.server}` : ''}` : null}</DialogDescription>
        </DialogHeader>
        {err && isApiError(err) && err.code === 'SOURCE_NOT_CONFIGURED' ? (
          <Callout tone="warn" title={t('lookup.record.notConfigured.title')}>
            <p>{t('lookup.record.notConfigured.body')}</p>
            <Link to={hubUrl('settings', 'connections', 'vnnic', { tab: 'whois' })} className="font-medium text-primary underline-offset-4 hover:underline">
              {t('lookup.record.notConfigured.action')}
            </Link>
          </Callout>
        ) : err ? (
          <DataError error={err} title={t('lookup.record.failed')} />
        ) : !rec ? (
          <Loading rows={4} />
        ) : (
          <div className="space-y-4">
            <Callout tone="info">{rec.notice[document.documentElement.lang === 'en' ? 'en' : 'vi']}</Callout>
            {rec.cached ? <p className="text-xs text-muted-foreground">{t('lookup.record.cached', { age: ageText(rec.age_ms) })}</p> : null}
            {rec.found ? (
              <KeyValue
                columns={2}
                rows={[
                  [t('lookup.record.source'), t(`lookup.source.${rec.source}`)],
                  [t('lookup.record.server'), rec.server],
                  [t('lookup.record.registrar'), rec.summary.registrar],
                  [t('lookup.record.status'), rec.summary.status.length ? rec.summary.status.join(', ') : null],
                  [t('lookup.record.created'), rec.summary.created ? formatDateTime(rec.summary.created) : null],
                  [t('lookup.record.expires'), rec.summary.expires ? formatDateTime(rec.summary.expires) : null],
                  [t('lookup.record.nameservers'), rec.summary.nameservers.length ? <span key="ns" className="font-mono text-xs">{rec.summary.nameservers.join(' · ')}</span> : null],
                  [t('lookup.record.dnssec'), rec.summary.dnssec],
                  rec.registrant_name ? [t('lookup.record.registrant'), rec.registrant_name] : false,
                ]}
              />
            ) : (
              <p className="text-sm">{t('lookup.record.notFound')}</p>
            )}
            {rec.registrant_hidden ? <p className="text-xs text-muted-foreground">{t('lookup.record.registrantHidden')}</p> : null}
            <details className="rounded-lg border p-3 text-sm">
              <summary className="cursor-pointer font-medium">{t('lookup.record.raw')}</summary>
              <pre className="mt-2 max-h-80 overflow-auto rounded bg-muted p-2 text-xs whitespace-pre-wrap break-all">
                {rec.text ?? JSON.stringify(rec.data, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
